import { useEffect } from 'react';

import {
  ActionStickerCardProps,
  addHttpIfMissing,
  BackrsFormValues,
  ContentNugget,
  NUGGET_TYPES,
  NUGGET_VARIANTS,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useDeletePriorityTaskMutation,
  useGetLinkPreviewQuery,
  useGetStatusTypesQuery,
  useGetTaskTypesQuery,
  usePutPriorityTaskMutation,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import {
  openPlaylistViewStep,
  closePlaylistDrawer,
  openEditTaskDialog,
  openNewTaskDialog,
  selectShouldAutoOpenPlaylistDrawer,
  setNuggetToAdd,
  openPlaylistListStep,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import {
  PriorityTask,
  PriorityTaskType,
  PRIORITY_TASK_TYPE_CODES,
  TaskTypeCode,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { UseFormReturn } from 'react-hook-form';

export const useDetermineNuggetTaskTypeId = (
  nugget: ContentNugget | null,
): PriorityTaskType | undefined => {
  const { data: taskTypes } = useGetTaskTypesQuery(undefined);

  if (!nugget) {
    return undefined;
  }

  const findTaskTypeByCode = (code: TaskTypeCode): PriorityTaskType | undefined => {
    return taskTypes?.find((taskType) => taskType.code === code);
  };

  if (
    nugget.variant === NUGGET_VARIANTS.SCHOLARSHIPS ||
    nugget.variant === NUGGET_VARIANTS.INTERNSHIP
  ) {
    return findTaskTypeByCode(PRIORITY_TASK_TYPE_CODES.PURSUE_OPPORTUNITY);
  }

  if (
    nugget.variant === NUGGET_VARIANTS.PROFILE_INTERNAL ||
    nugget.variant === NUGGET_VARIANTS.PROFILE_EXTERNAL
  ) {
    return findTaskTypeByCode(PRIORITY_TASK_TYPE_CODES.CONNECT);
  }

  if (nugget.type === NUGGET_TYPES.ACTIVITY) {
    return findTaskTypeByCode(PRIORITY_TASK_TYPE_CODES.ENRICHMENT);
  }

  if (nugget.variant === NUGGET_VARIANTS.EDUCATIONAL_INVESTMENT) {
    return findTaskTypeByCode(PRIORITY_TASK_TYPE_CODES.ENRICHMENT);
  }
  if (nugget.type === NUGGET_TYPES.TOOL) {
    return findTaskTypeByCode(PRIORITY_TASK_TYPE_CODES.USE_A_TOOL);
  }

  const defaultTaskType = findTaskTypeByCode(PRIORITY_TASK_TYPE_CODES.EXPLORE);

  return defaultTaskType;
};

type PlaylistChangeHandlerProps = Readonly<{
  playlistId: string;
  playlists: PriorityPlaylist[];
  shouldPlaylistDrawerChange: boolean;
}>;

export const usePlaylistChangeHandler = ({
  playlistId,
  playlists,
  shouldPlaylistDrawerChange,
}: PlaylistChangeHandlerProps) => {
  const dispatch = useAppDispatch();
  const shouldAutoOpenPlaylistDrawer = useAppSelector(selectShouldAutoOpenPlaylistDrawer);

  useEffect(() => {
    // if we're editing a task, we don't want to change the playlist drawer
    if (!shouldPlaylistDrawerChange) {
      return;
    }

    // Use Redux state to determine if we should auto-open the playlist drawer
    if (playlistId && shouldAutoOpenPlaylistDrawer) {
      const selectedPlaylist = playlists.find((playlist) => playlist.id === parseInt(playlistId));

      dispatch(openPlaylistViewStep(selectedPlaylist || null));
    }

    if (!playlistId && shouldAutoOpenPlaylistDrawer) {
      dispatch(closePlaylistDrawer());
    }
  }, [playlistId, playlists, dispatch, shouldAutoOpenPlaylistDrawer, shouldPlaylistDrawerChange]);
};

export const useGetTaskActionsAndProperties = () => {
  const dispatch = useAppDispatch();

  const { data: taskTypes } = useGetTaskTypesQuery(undefined);
  const { data: taskStatuses } = useGetStatusTypesQuery(undefined);

  const [deleteTask] = useDeletePriorityTaskMutation();
  const [updateTask] = usePutPriorityTaskMutation();

  const handleSetNuggetToAdd = (nugget: ContentNugget | undefined) => {
    if (nugget) {
      dispatch(setNuggetToAdd(nugget));
    }
  };

  const handleOpenEditTaskDialog = (task: PriorityTask, shouldAutoOpenPlaylistDrawer = true) => {
    dispatch(openEditTaskDialog({ task, shouldAutoOpenPlaylistDrawer }));
  };

  const handleOpenNewTaskDialog = (
    playlist: PriorityPlaylist | null,
    shouldAutoOpenPlaylistDrawer = true,
    preloadedTaskInfo?: Partial<PriorityTask>,
  ) => {
    dispatch(openNewTaskDialog({ playlist, shouldAutoOpenPlaylistDrawer, preloadedTaskInfo }));

    if (playlist && shouldAutoOpenPlaylistDrawer) {
      dispatch(openPlaylistViewStep(playlist));
    }
  };

  const openToolNuggetAsTask = (sticker: ActionStickerCardProps) => {
    if (!sticker.tool) {
      alert('No tool found');

      return;
    }

    const toolAsNugget: ContentNugget = {
      id: sticker.tool.id,
      slug: sticker.tool.tool_id,
      title: sticker.tool.title,
      description: sticker.tool.description,
      star_rating: 0,
      value_rating: 0,
      variant: 'tool',
      is_browsable: false,
      tool_info: sticker.tool,
      type: NUGGET_TYPES.TOOL,
    };

    dispatch(openPlaylistListStep());
    handleSetNuggetToAdd(toolAsNugget);
  };

  return {
    taskTypes: taskTypes || [],
    taskStatuses: taskStatuses || [],
    deleteTask,
    handleOpenEditTaskDialog,
    handleOpenNewTaskDialog,
    handleSetNuggetToAdd,
    updateTask,
    openToolNuggetAsTask,
  };
};

export const useTransformTagsForEdit = (
  methods: UseFormReturn<BackrsFormValues>,
  taskToEdit?: PriorityTask | null,
  preloadedTaskInfo?: Partial<PriorityTask>,
) => {
  useEffect(() => {
    const getTagTitles = (): string[] => {
      if (taskToEdit?.tags?.length) {
        return taskToEdit.tags.map((tag) => tag.title);
      }

      if (preloadedTaskInfo?.tags?.length) {
        return preloadedTaskInfo.tags.map((tag) => (typeof tag === 'string' ? tag : tag.title));
      }

      return [];
    };

    const tagTitles = getTagTitles();

    if (tagTitles.length > 0) {
      // Use type assertion to work around BackrsFormValues type limitation
      (methods.setValue as (name: string, value: unknown) => void)('tags', tagTitles);
    }
  }, [taskToEdit, preloadedTaskInfo, methods]);
};

// A preview without a title still needs a label, and the host is what a reader recognises.
const linkHostname = (url: string): string => {
  try {
    return new URL(addHttpIfMissing(url)).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/**
 * Presents a task's plain `content_url` as a nugget so it renders beside attached content
 * instead of needing its own affordance. Null when the task has no link.
 */
export const useTaskLinkNugget = (task: PriorityTask | null | undefined): ContentNugget | null => {
  const { data: linkPreview } = useGetLinkPreviewQuery(task?.content_url || '', {
    skip: !task?.content_url,
  });

  if (!task?.content_url) {
    return null;
  }

  return {
    id: 0,
    slug: '',
    title: linkPreview?.title || linkHostname(task.content_url),
    description: linkPreview?.description || '',
    external_url: task.content_url,
    // No variant means "external link". 'resource' is the closest; 'video-external'
    // labels every link "Video", and its thumbnail lookup is not why the image
    // resolves — the card's default branch reads preview_media for any variant.
    variant: 'resource',
    is_browsable: true,
    type: 'content',
    star_rating: 0,
    value_rating: 0,
    preview_media: {
      id: 0,
      media_url: linkPreview?.images?.[0] || linkPreview?.favicons?.[0] || '',
    },
  };
};
